import { useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useTabList, useTab, useTabPanel } from 'react-aria';
import { Item, useTabListState } from 'react-stately';
import { jsx, jsxs } from 'react/jsx-runtime';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

var themes = ['light', 'dark'];
var prefix = 'forno-tabs';

var _excluded = ["children"];

function AriaTabs(props) {
  var state = useTabListState(props);
  var ref = useRef();

  var _useTabList = useTabList(props, state, ref),
      tabListProps = _useTabList.tabListProps;

  var className = props.className,
      theme = props.theme,
      id = props.id;
  var customClassName = classnames(prefix, className);
  var tabPanelKey = state.selectedItem ? state.selectedItem.key : _toConsumableArray(state.collection)[0].key;
  return /*#__PURE__*/jsxs("div", {
    className: customClassName,
    "data-theme": theme,
    "data-testid": "Tabs",
    id: id,
    children: [/*#__PURE__*/jsx("div", _objectSpread2(_objectSpread2({}, tabListProps), {}, {
      ref: ref,
      id: "".concat(id, "-list"),
      children: _toConsumableArray(state.collection).map(function (item) {
        return /*#__PURE__*/jsx(Tab, {
          item: item,
          state: state,
          id: "".concat(id, "-tab"),
          ariaControls: item.key
        }, item.key);
      })
    })), /*#__PURE__*/jsx(TabPanel, {
      state: state,
      id: "".concat(id, "-tabPanel"),
      ariaLabelledBy: tabPanelKey
    }, tabPanelKey)]
  });
}

AriaTabs.propTypes = {
  className: PropTypes.string,
  theme: PropTypes.oneOf(themes),
  id: PropTypes.string.isRequired
};
AriaTabs.defaultProps = {
  className: '',
  theme: 'light'
};

function Tab(_ref) {
  var item = _ref.item,
      state = _ref.state,
      id = _ref.id,
      ariaControls = _ref.ariaControls;
  var rendered = item.rendered,
      key = item.key;
  var ref = useRef();

  var _useTab = useTab({
    key: key
  }, state, ref),
      tabProps = _useTab.tabProps;

  return /*#__PURE__*/jsx("div", _objectSpread2(_objectSpread2({}, tabProps), {}, {
    ref: ref,
    className: "font-block-s-sb",
    id: id,
    "aria-controls": ariaControls,
    children: rendered
  }));
}

Tab.propTypes = {
  item: PropTypes.shape({
    rendered: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired
  }).isRequired,
  state: PropTypes.shape({}).isRequired,
  id: PropTypes.string.isRequired,
  ariaControls: PropTypes.string.isRequired
};

function TabPanel(props) {
  var ref = useRef();
  var theme = props.theme,
      state = props.state,
      id = props.id,
      ariaLabelledBy = props.ariaLabelledBy;

  var _useTabPanel = useTabPanel(props, state, ref),
      tabPanelProps = _useTabPanel.tabPanelProps;

  return /*#__PURE__*/jsx("div", _objectSpread2(_objectSpread2({}, tabPanelProps), {}, {
    ref: ref,
    "data-theme": theme,
    id: id,
    "aria-labelledby": ariaLabelledBy,
    children: state.selectedItem ? state.selectedItem.props.children : null
  }));
}

TabPanel.propTypes = {
  state: PropTypes.shape({
    selectedItem: PropTypes.shape({
      props: PropTypes.shape({
        children: PropTypes.node
      })
    })
  }).isRequired,
  theme: PropTypes.oneOf(themes),
  id: PropTypes.string.isRequired,
  ariaLabelledBy: PropTypes.string.isRequired
};
TabPanel.defaultProps = {
  theme: 'light'
};
function Tabs(_ref2) {
  var children = _ref2.children,
      props = _objectWithoutProperties(_ref2, _excluded);

  return /*#__PURE__*/jsx(AriaTabs, _objectSpread2(_objectSpread2({}, props), {}, {
    children: children
  }));
}
Tabs.propTypes = {
  children: PropTypes.node.isRequired
};
Tabs.Item = Item;

export { Tabs as default };
